import { Money } from '@obraplan/core/domain/shared/money';
import {
  EMPLOYEE_CHARGES_MAX_PERCENT,
  type EmployeeStatus,
} from '@obraplan/core/domain/workforce/employee';
import {
  employeeHourlyRate,
  monthlyCostWithCharges,
} from '@obraplan/core/domain/workforce/labor-cost';

/**
 * O custo da folha como a tela de mão de obra o mostra (Fase 29, etapa 3 — D12).
 *
 * O encargo é um % por funcionário, e a regra da D12 é que ele vale **em todo lugar**: no
 * custo/hora da linha, no custo da equipe, na média do quadro de efetivo — e, na outra ponta,
 * no R$/h da função que o orçamento usa. Uma tela que somasse o salário puro enquanto o
 * orçamento soma com encargo mostraria duas obras diferentes pela mesma folha. Por isso as
 * contas daqui passam só por `employeeHourlyRate` e `monthlyCostWithCharges`, e nenhuma
 * refaz a multiplicação por conta própria.
 *
 * Também mora aqui o que o formulário faz com o número digitado — sem React, para o teste
 * provar a regra sem montar o diálogo.
 */

/** O que estas contas leem do funcionário — o `Employee` serve, e o teste não precisa dele inteiro. */
export interface FuncionarioDaFolha {
  readonly status: EmployeeStatus;
  readonly monthlySalary?: Money;
  readonly chargesPercent?: number;
}

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

/**
 * `"2026-08-20"` e `"2026-08"` viram `"agosto/2026"`.
 *
 * O custo da hora é do mês (RN-059): a tela de mão de obra e o orçamento nomeiam o mês da
 * conta, para o número que muda na virada não parecer erro.
 */
export function mesPorExtenso(data: string): string {
  const [ano, mes] = data.split('-').map(Number);
  if (ano === undefined || mes === undefined || Number.isNaN(ano) || Number.isNaN(mes)) {
    return data;
  }
  const nome = MESES[mes - 1];
  return nome === undefined ? data : `${nome}/${String(ano)}`;
}

/** `80` vira `"80%"`, `80.5` vira `"80,5%"` — o encargo ao lado do salário. */
export function textoDoEncargo(percentual: number): string {
  return `${percentual.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

/**
 * `80.5` vira `"80,5"` — o número gravado de volta no campo, para ser editado e reenviado.
 *
 * Com vírgula, e sem milhar: o formulário lê `"3.520,50"` à brasileira, e o ponto de
 * `"3520.5"` seria lido como separador de milhar.
 */
export function numeroNoCampo(percentual: number): string {
  return String(percentual).replace('.', ',');
}

/** O campo do encargo lido: o número (ou nada, em branco), ou o porquê de não servir. */
export type EncargoDigitado =
  | { readonly ok: true; readonly valor: number | undefined }
  | { readonly ok: false; readonly erro: string };

/**
 * O campo "Encargos (%)": `"80"`, `"80,5"`, `"80.5"` e `"80%"` são o mesmo número.
 *
 * Em branco é "não informado" — a conta segue com o salário puro, e a lista avisa (D12).
 * Nunca vira zero: zero de encargo é uma afirmação, branco é uma falta. O número sai com as
 * duas casas que o cadastro guarda, para o que se vê ser o que fica gravado.
 */
export function lerEncargo(texto: string): EncargoDigitado {
  const limpo = texto.replace(/[\s%]/g, '');
  if (limpo === '') return { ok: true, valor: undefined };
  const valor = /^\d+([.,]\d+)?$/.test(limpo) ? Number(limpo.replace(',', '.')) : Number.NaN;
  if (!Number.isFinite(valor) || valor > EMPLOYEE_CHARGES_MAX_PERCENT) {
    return {
      ok: false,
      erro: `O encargo vai de 0 a ${String(EMPLOYEE_CHARGES_MAX_PERCENT)}% — digite como 80 ou 80,5.`,
    };
  }
  return { ok: true, valor: Math.round(valor * 100) / 100 };
}

/**
 * O ativo com salário e sem encargo: o custo dele existe, mas está **subestimado** — e a
 * lista diz isso ao lado do salário, em vez de deixar o número parecer completo (D12).
 */
export function semEncargoInformado(funcionario: FuncionarioDaFolha): boolean {
  return (
    funcionario.status === 'ACTIVE' &&
    funcionario.monthlySalary !== undefined &&
    funcionario.chargesPercent === undefined
  );
}

/** O que o quadro de efetivo diz da folha dos ativos. */
export interface CustoDaFolha {
  /** Quantos ativos têm salário — os únicos que entram na conta (RN-055). */
  readonly comSalario: number;
  /** A soma do custo do mês com encargos. Zero quando ninguém tem salário. */
  readonly custoDoMes: Money;
  /** A média do custo da hora com encargos; ausente sem salário ou sem hora útil no mês. */
  readonly mediaPorHora?: Money;
  /** Quantos ativos com salário estão sem encargo informado. */
  readonly semEncargo: number;
}

/**
 * A folha do mês e o custo médio da hora, **com encargos** (D12).
 *
 * A média é a do custo do mês dividido pelas horas úteis, a mesma conta do R$/h da função no
 * orçamento (D11) — a diferença é que aqui é a obra inteira, e lá cada função.
 */
export function custoDaFolha(
  funcionarios: readonly FuncionarioDaFolha[],
  quartosDeHoraDoMes: number,
): CustoDaFolha {
  const ativos = funcionarios.filter((funcionario) => funcionario.status === 'ACTIVE');
  const custos = ativos.flatMap((funcionario) => {
    const custo = monthlyCostWithCharges(funcionario.monthlySalary, funcionario.chargesPercent);
    return custo === undefined ? [] : [custo];
  });
  const custoDoMes = Money.sum(custos);
  const horas = quartosDeHoraDoMes / 4;
  return {
    comSalario: custos.length,
    custoDoMes,
    ...(custos.length > 0 && horas > 0
      ? { mediaPorHora: custoDoMes.times(1 / (custos.length * horas)) }
      : {}),
    semEncargo: ativos.filter(semEncargoInformado).length,
  };
}

/**
 * O que a equipe custa por hora: a soma do custo da hora, com encargos, de quem tem salário.
 *
 * Ausente quando ninguém da equipe tem salário — "R$ 0,00/h" diria que a equipe é de graça.
 */
export function custoDaEquipePorHora(
  integrantes: readonly FuncionarioDaFolha[],
  quartosDeHoraDoMes: number,
): Money | undefined {
  const porHora = integrantes.flatMap((funcionario) => {
    const taxa = employeeHourlyRate(funcionario, quartosDeHoraDoMes);
    return taxa === undefined ? [] : [taxa.perHour];
  });
  return porHora.length === 0 ? undefined : Money.sum(porHora);
}
